import { type Address, type WalletClient, formatUnits, erc20Abi } from "viem";
import { getTokenAddress, publicClients } from "@/config";
import { TOKEN_IDS, type TokenId } from "@/types";
import { TOKEN_CONFIG } from "@/config";
import { mainnet } from "viem/chains";

export interface TokenBalancesResponse {
    tokenId: TokenId;
    tokenBalance: string;
}

export const fetchTokenBalances = async (
    address: Address,
    walletClient?: WalletClient,
): Promise<TokenBalancesResponse[]> => {
    const chainId = walletClient?.chain?.id ?? mainnet.id;

    try {
        const viemTransactions = publicClients[mainnet.id].multicall({
            contracts: Object.values(TOKEN_IDS).map((tokenId) => ({
                address: getTokenAddress(chainId, tokenId),
                abi: erc20Abi,
                functionName: "balanceOf",
                args: [address],
            })),
        });

        const results = await viemTransactions;

        if (!results || results.length === 0) {
            throw new Error("No balance results returned");
        }

        return Object.values(TOKEN_IDS).map((tokenId, index) => {
            const decimals = TOKEN_CONFIG[tokenId].decimals;

            try {
                const { result, status } = results[index];

                if (result === null || result === undefined) {
                    throw new Error(`Balance for token ${tokenId} is null or undefined`);
                }

                if (status !== "success") {
                    throw new Error(`Failed to fetch balance for token ${tokenId}`);
                }

                const formattedBalance = formatUnits(BigInt(result), decimals);

                return {
                    tokenId,
                    tokenBalance: formattedBalance,
                };
            } catch (error) {
                console.warn(`Error processing balance for token ${tokenId}:`, error);
                return {
                    tokenId,
                    tokenBalance: "0.00",
                };
            }
        });
    } catch (error) {
        console.error("Error fetching all token balances:", error);
        throw error;
    }
};
